import { writable } from 'svelte/store';
import { generateId } from '$lib/utils';
import { getToastVoice } from '$lib/utils/voice';

/**
 * Toast notification interface
 */
export interface ToastNotification {
  id: string;
  type: 'success' | 'error' | 'warning' | 'info';
  message: string;
  duration?: number;
  dismissible?: boolean;
}

/**
 * Voice category types for semantic toast messages
 */
type VoiceCategory = 'auth' | 'upload' | 'analysis' | 'general';

/**
 * Toast store for managing notifications
 */
function createToastStore() {
  const { subscribe, update } = writable<ToastNotification[]>([]);

  /**
   * Add a new toast notification
   */
  function addToast(toast: Omit<ToastNotification, 'id'>): string {
    const id = generateId();
    const newToast: ToastNotification = {
      id,
      duration: 5000,
      dismissible: true,
      ...toast,
    };

    update(toasts => [...toasts, newToast]);
    return id;
  }

  /**
   * Remove a toast notification by ID
   */
  function removeToast(id: string): void {
    update(toasts => toasts.filter(toast => toast.id !== id));
  }

  /**
   * Clear all toast notifications
   */
  function clearAll(): void {
    update(() => []);
  }

  /**
   * Convenience methods for different toast types
   */
  function success(
    message: string,
    options?: Partial<Omit<ToastNotification, 'id' | 'type' | 'message'>>
  ): string {
    return addToast({ type: 'success', message, ...options });
  }

  function error(
    message: string,
    options?: Partial<Omit<ToastNotification, 'id' | 'type' | 'message'>>
  ): string {
    return addToast({ type: 'error', message, ...options });
  }

  function warning(
    message: string,
    options?: Partial<Omit<ToastNotification, 'id' | 'type' | 'message'>>
  ): string {
    return addToast({ type: 'warning', message, ...options });
  }

  function info(
    message: string,
    options?: Partial<Omit<ToastNotification, 'id' | 'type' | 'message'>>
  ): string {
    return addToast({ type: 'info', message, ...options });
  }

  /**
   * Voice-aware toast methods
   * Use these for user-facing acknowledgements to get the Quilty voice.
   * 
   * @example
   * toastStore.voice.success('auth', 'login_success')
   * toastStore.voice.error('upload', 'error')
   */
  const voice = {
    success(
      category: VoiceCategory,
      key: string,
      variables?: Record<string, string>,
      options?: Partial<Omit<ToastNotification, 'id' | 'type' | 'message'>>
    ): string {
      const message = getToastVoice(category, key, variables);
      return success(message, options);
    },

    error(
      category: VoiceCategory,
      key: string,
      variables?: Record<string, string>,
      options?: Partial<Omit<ToastNotification, 'id' | 'type' | 'message'>>
    ): string {
      const message = getToastVoice(category, key, variables);
      return error(message, options);
    },

    warning(
      category: VoiceCategory,
      key: string,
      variables?: Record<string, string>,
      options?: Partial<Omit<ToastNotification, 'id' | 'type' | 'message'>>
    ): string {
      const message = getToastVoice(category, key, variables);
      return warning(message, options);
    },

    info(
      category: VoiceCategory,
      key: string,
      variables?: Record<string, string>,
      options?: Partial<Omit<ToastNotification, 'id' | 'type' | 'message'>>
    ): string {
      const message = getToastVoice(category, key, variables);
      return info(message, options);
    },
  };

  return {
    subscribe,
    addToast,
    removeToast,
    clearAll,
    success,
    error,
    warning,
    info,
    voice,
  };
}

/**
 * Toast store instance
 */
export const toastStore = createToastStore();
